import { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";

const ThemeContext = createContext(null);

// onBrand = text color to use on top of the primary colour (for WCAG contrast)
export const THEMES = {
  // Light
  "light-blue":    { mode: "light", primary: "#0044FF", primaryHover: "#0033CC", accent: "#FF4D00", onBrand: "#FFFFFF", label: "Electric Blue" },
  "light-red":     { mode: "light", primary: "#DC2626", primaryHover: "#B91C1C", accent: "#F59E0B", onBrand: "#FFFFFF", label: "Sunset Red" },
  "light-green":   { mode: "light", primary: "#059669", primaryHover: "#047857", accent: "#0891B2", onBrand: "#FFFFFF", label: "Forest" },
  "light-purple":  { mode: "light", primary: "#7C3AED", primaryHover: "#6D28D9", accent: "#EC4899", onBrand: "#FFFFFF", label: "Amethyst" },
  "light-teal":    { mode: "light", primary: "#0D9488", primaryHover: "#0F766E", accent: "#F97316", onBrand: "#FFFFFF", label: "Lagoon" },
  "light-rose":    { mode: "light", primary: "#E11D48", primaryHover: "#BE123C", accent: "#8B5CF6", onBrand: "#FFFFFF", label: "Rose Gold" },
  "light-mono":    { mode: "light", primary: "#0F172A", primaryHover: "#020617", accent: "#F59E0B", onBrand: "#FFFFFF", label: "Monochrome" },
  "light-sand":    { mode: "light", primary: "#C2410C", primaryHover: "#9A3412", accent: "#0D9488", onBrand: "#FFFFFF", label: "Sand Dune" },

  // Dark
  "dark-blue":     { mode: "dark", primary: "#3B82F6", primaryHover: "#2563EB", accent: "#F97316", onBrand: "#FFFFFF", label: "Midnight Blue" },
  "dark-red":      { mode: "dark", primary: "#EF4444", primaryHover: "#DC2626", accent: "#F59E0B", onBrand: "#FFFFFF", label: "Ember" },
  "dark-green":    { mode: "dark", primary: "#10B981", primaryHover: "#059669", accent: "#06B6D4", onBrand: "#062A21", label: "Matrix Green" },
  "dark-purple":   { mode: "dark", primary: "#A78BFA", primaryHover: "#8B5CF6", accent: "#EC4899", onBrand: "#1E1B4B", label: "Neon Purple" },
  "dark-cyber":    { mode: "dark", primary: "#06FFA5", primaryHover: "#00E094", accent: "#FF00A8", onBrand: "#02150E", label: "Cyberpunk" },
  "dark-solar":    { mode: "dark", primary: "#EAB308", primaryHover: "#CA8A04", accent: "#F97316", onBrand: "#1C1917", label: "Solar Flare" },
  "dark-ocean":    { mode: "dark", primary: "#38BDF8", primaryHover: "#0EA5E9", accent: "#A855F7", onBrand: "#0C1F2E", label: "Deep Ocean" },
  "dark-carbon":   { mode: "dark", primary: "#E2E8F0", primaryHover: "#CBD5E1", accent: "#F59E0B", onBrand: "#0A0A0A", label: "Carbon" },
};

// Font families — combos of heading + body
export const FONTS = {
  "grotesk":   { heading: "'Cabinet Grotesk', 'Satoshi', system-ui, sans-serif", body: "'Inter', system-ui, sans-serif", label: "Cabinet Grotesk × Inter", vibe: "Modern SaaS" },
  "serif":     { heading: "'Playfair Display', Georgia, serif",                  body: "'Inter', system-ui, sans-serif", label: "Playfair × Inter",         vibe: "Editorial" },
  "mono":      { heading: "'JetBrains Mono', 'Fira Code', monospace",            body: "'Inter', system-ui, sans-serif", label: "JetBrains Mono × Inter",   vibe: "Developer" },
  "geometric": { heading: "'Space Grotesk', system-ui, sans-serif",              body: "'Space Grotesk', system-ui, sans-serif", label: "Space Grotesk",     vibe: "Geometric" },
  "humanist":  { heading: "'DM Serif Display', Georgia, serif",                  body: "'DM Sans', system-ui, sans-serif", label: "DM Serif × DM Sans",     vibe: "Humanist" },
  "brutalist": { heading: "'Archivo Black', 'Impact', sans-serif",               body: "'IBM Plex Sans', system-ui, sans-serif", label: "Archivo Black × IBM Plex", vibe: "Brutalist" },
  "playful":   { heading: "'Fraunces', Georgia, serif",                          body: "'Fraunces', Georgia, serif",     label: "Fraunces",                 vibe: "Playful" },
  "system":    { heading: "system-ui, -apple-system, sans-serif",                body: "system-ui, -apple-system, sans-serif", label: "System Default",     vibe: "Native" },
};

const LIGHT_TOKENS = { bg: "#F8F9FA", surface: "#FFFFFF", border: "#E2E8F0", text: "#0A0F29", textSoft: "#64748B" };
const DARK_TOKENS  = { bg: "#0A0F29", surface: "#111827", border: "#1F2937", text: "#F1F5F9", textSoft: "#94A3B8" };

const DARK_VARIANTS = {
  "dark-cyber":  { bg: "#02050D", surface: "#0A0F1F", border: "#1B2244", text: "#E6FFF5", textSoft: "#7EE8C6" },
  "dark-ocean":  { bg: "#020617", surface: "#0F172A", border: "#1E293B", text: "#E0F2FE", textSoft: "#7DD3FC" },
  "dark-carbon": { bg: "#0A0A0A", surface: "#171717", border: "#262626", text: "#F5F5F5", textSoft: "#A3A3A3" },
  "dark-solar":  { bg: "#1C1917", surface: "#292524", border: "#3F3F3F", text: "#FEF3C7", textSoft: "#D6D3D1" },
};

function tokensFor(themeKey, mode) {
  if (mode === "dark") return DARK_VARIANTS[themeKey] || DARK_TOKENS;
  return LIGHT_TOKENS;
}

export function ThemeProvider({ children }) {
  const { user, updateProfile } = useAuth();
  const storedTheme = localStorage.getItem("ld_theme") || "light-blue";
  const storedFont  = localStorage.getItem("ld_font")  || "grotesk";
  const [themeKey, setThemeKey] = useState(user?.theme || storedTheme);
  const [fontKey,  setFontKey]  = useState(user?.font  || storedFont);

  useEffect(() => {
    if (user?.theme && user.theme !== themeKey) setThemeKey(user.theme);
    if (user?.font  && user.font  !== fontKey)  setFontKey(user.font);
  }, [user?.theme, user?.font]); // eslint-disable-line

  useEffect(() => {
    const t = THEMES[themeKey] || THEMES["light-blue"];
    const f = FONTS[fontKey]   || FONTS["grotesk"];
    const tokens = tokensFor(themeKey, t.mode);
    const r = document.documentElement;
    r.style.setProperty("--bg", tokens.bg);
    r.style.setProperty("--surface", tokens.surface);
    r.style.setProperty("--border", tokens.border);
    r.style.setProperty("--text", tokens.text);
    r.style.setProperty("--text-soft", tokens.textSoft);
    r.style.setProperty("--brand", t.primary);
    r.style.setProperty("--brand-hover", t.primaryHover);
    r.style.setProperty("--on-brand", t.onBrand || "#FFFFFF");
    r.style.setProperty("--warn", t.accent);
    r.style.setProperty("--font-heading", f.heading);
    r.style.setProperty("--font-body", f.body);
    r.setAttribute("data-theme", t.mode);
    r.classList.toggle("dark", t.mode === "dark");
    localStorage.setItem("ld_theme", themeKey);
    localStorage.setItem("ld_font", fontKey);
  }, [themeKey, fontKey]);

  const setTheme = async (k) => {
    setThemeKey(k);
    if (user) { try { await updateProfile({ theme: k }); } catch (e) { /* ignore */ } }
  };
  const setFont = async (k) => {
    setFontKey(k);
    if (user) { try { await updateProfile({ font: k }); } catch (e) { /* ignore */ } }
  };

  const setMode = async (mode) => {
    if (mode === "dark") {
      const candidate = themeKey.replace("light-", "dark-");
      const nextKey = THEMES[candidate] ? candidate : (
        themeKey === "light-teal" ? "dark-ocean" :
        themeKey === "light-sand" ? "dark-solar" :
        themeKey === "light-mono" ? "dark-carbon" :
        "dark-blue"
      );
      await setTheme(nextKey);
    } else {
      const candidate = themeKey.replace("dark-", "light-");
      const nextKey = THEMES[candidate] ? candidate : (
        themeKey === "dark-cyber" ? "light-teal" :
        themeKey === "dark-solar" ? "light-sand" :
        themeKey === "dark-ocean" ? "light-teal" :
        themeKey === "dark-carbon" ? "light-mono" :
        "light-blue"
      );
      await setTheme(nextKey);
    }
  };

  const toggleMode = async () => {
    const currentMode = (THEMES[themeKey] || THEMES["light-blue"]).mode;
    await setMode(currentMode === "dark" ? "light" : "dark");
  };

  const currentTheme = THEMES[themeKey] || THEMES["light-blue"];

  return (
    <ThemeContext.Provider value={{
      themeKey, setTheme, theme: currentTheme,
      mode: currentTheme.mode, isDark: currentTheme.mode === "dark",
      setMode, toggleMode,
      fontKey,  setFont,  font: FONTS[fontKey] || FONTS["grotesk"],
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext) || {
  themeKey: "light-blue", setTheme: () => {}, theme: THEMES["light-blue"],
  mode: "light", isDark: false, setMode: () => {}, toggleMode: () => {},
  fontKey:  "grotesk",    setFont:  () => {}, font:  FONTS["grotesk"],
};
